import styled from '@emotion/styled'
import { ReactNode, useState } from 'react'

import { CloseButtonDialogBody } from './CloseButtonDialogBody'
import { Dialog } from './Dialog'
import { Color, theme } from '../../constants'
import { useTranslations } from '../../translate'
import { Button, ButtonDesign } from '../Button'
import { Flex } from '../Flex'
import { Paragraph } from '../Paragraph'

const Title = styled(Dialog.Title)({
  color: Color.BLUE,
  marginRight: theme.spacing[3],
  marginBottom: 0,
  fontSize: theme.font.size[6],
  fontWeight: theme.font.weight.bold,
  letterSpacing: '0.03em',
  textTransform: 'uppercase',
})

type ConfirmationDialogBodyProps = {
  buttonText?: string
  buttonDesign?: ButtonDesign
  disableButton?: boolean
  children?: ReactNode
  description?: string
  heading: string
  onClose: () => void
  onConfirmClick: (() => void) | (() => Promise<void>)
  autoClose: boolean
}

export const ConfirmationDialogBody = (props: ConfirmationDialogBodyProps) => {
  const t = useTranslations()
  const [isLoading, setIsLoading] = useState(false)

  const handleConfirmClick = async () => {
    setIsLoading(true)

    try {
      await props.onConfirmClick()
      if (props.autoClose) props.onClose()
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <CloseButtonDialogBody styles={{ maxWidth: 500 }}>
      <Flex col gap={3}>
        <Title>{props.heading}</Title>

        {props.description && <Paragraph m={0}>{props.description}</Paragraph>}

        {props.children}

        <Flex gap={4}>
          <Button
            design={props.buttonDesign ?? 'danger'}
            loading={isLoading}
            onClick={handleConfirmClick}
            disabled={props.disableButton}
          >
            {props.buttonText ?? t.ui.confirm()}
          </Button>
          <Button design="primary" onClick={props.onClose} text>
            {t.ui.cancel()}
          </Button>
        </Flex>
      </Flex>
    </CloseButtonDialogBody>
  )
}
